import React, { useEffect, useRef, useState } from "react";

import type { MeetingStatus } from "@performyard/models";
import { ButtonStandard, Checkbox, Text } from "@performyard/py-components";

import ContentModal from "../../../../components/Modal/Templates/ContentModal";

type StatusConfirmationModalProps = {
	isOpen: boolean;
	isLoading: boolean;
	status: MeetingStatus | null;
	action?: "status-change" | "remove-from-calendar";
	onClose: () => void;
	onConfirm: (removeFromCalendar?: boolean) => void;
	hasConnectedCalendarEvent?: boolean;
	meetingName?: string;
};

type ActionConfig = {
	title: string;
	message: string;
	confirmText: string;
	confirmButtonColor: "green" | "red";
};

const getActionConfig = (
	action: "status-change" | "remove-from-calendar",
	status: MeetingStatus | null
): ActionConfig | null => {
	if (action === "remove-from-calendar") {
		return {
			title: "Remove Meeting From Calendar?",
			message:
				"Do you want to remove this meeting from your calendar? This will delete the event, but you can always add a new one later.",
			confirmText: "Yes, Remove",
			confirmButtonColor: "red",
		};
	}

	switch (status) {
		case "canceled":
			return {
				title: "Cancel Meeting?",
				message:
					"All meeting notes, agenda, and action items will be placed in read-only mode and no longer configurable.",
				confirmText: "Yes, Cancel Meeting",
				confirmButtonColor: "green",
			};
		case "completed":
			return {
				title: "Complete Meeting?",
				message: "Are you sure you want to complete this meeting?",
				confirmText: "Yes, Complete Meeting",
				confirmButtonColor: "green",
			};
		case "archived":
			return {
				title: "Archive Meeting?",
				message:
					"All meeting notes, agenda, and action items will be placed in read-only mode and no longer configurable.",
				confirmText: "Yes, Archive Meeting",
				confirmButtonColor: "green",
			};
		default:
			return null;
	}
};

export const StatusConfirmationModal = ({
	isOpen,
	isLoading,
	status,
	action = "status-change",
	onClose,
	onConfirm,
	hasConnectedCalendarEvent = false,
	meetingName,
}: StatusConfirmationModalProps): React.ReactElement => {
	const [removeFromCalendar, setRemoveFromCalendar] = useState(false);

	const contentRef = useRef<{
		config: ActionConfig | null;
		showCheckbox: boolean;
		meetingName: string | undefined;
	}>({ config: null, showCheckbox: false, meetingName: undefined });

	useEffect(() => {
		if (isOpen) {
			const config = getActionConfig(action, status);
			const showCheckbox = action === "status-change" && status === "canceled" && hasConnectedCalendarEvent;
			contentRef.current = { config, showCheckbox, meetingName };
		}
	}, [isOpen, action, status, hasConnectedCalendarEvent, meetingName]);

	const config = isOpen ? getActionConfig(action, status) : contentRef.current.config;
	const showCalendarCheckbox = isOpen
		? action === "status-change" && status === "canceled" && hasConnectedCalendarEvent
		: contentRef.current.showCheckbox;
	const displayMeetingName = isOpen ? meetingName : contentRef.current.meetingName;

	const handleConfirm = (): void => {
		onConfirm(removeFromCalendar);
	};

	const handleClose = (): void => {
		setRemoveFromCalendar(false);
		onClose();
	};

	return (
		<ContentModal
			isOpen={isOpen}
			handleClose={isLoading ? () => {} : handleClose}
			size="large"
			title={
				<div className="flex flex-col items-center">
					<Text size="sub-heading" weight="semi-bold">
						{config?.title}
					</Text>
					{displayMeetingName && (
						<Text size="paragraph-2" weight="regular" color="neutral-6">
							{displayMeetingName}
						</Text>
					)}
				</div>
			}
			buttons={[
				<div key="cancel">
					<ButtonStandard
						variant="solid"
						color="primary"
						size="large"
						onClick={handleClose}
						disabled={isLoading}
					>
						Cancel
					</ButtonStandard>
				</div>,
				<div key="confirm">
					<ButtonStandard
						variant="solid"
						color={config?.confirmButtonColor}
						size="large"
						onClick={handleConfirm}
						disabled={isLoading}
					>
						{config?.confirmText}
					</ButtonStandard>
				</div>,
			]}
		>
			<div className="space-y-4 text-center">
				<Text size="paragraph-1" weight="regular">
					{config?.message}
				</Text>
				{showCalendarCheckbox && (
					<div className="flex items-center justify-center space-x-2">
						<Checkbox
							id="remove-from-calendar"
							checked={removeFromCalendar}
							onCheckedChange={setRemoveFromCalendar}
							disabled={isLoading}
							aria-label="Remove from My Calendar"
						/>
						<Text size="paragraph-3" weight="regular">
							Remove from My Calendar
						</Text>
					</div>
				)}
			</div>
		</ContentModal>
	);
};
